import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.scss';
import type { IRichTextEditorProps, IRichTextEditorRef } from './interfaces';
import { sanitizeHTML } from './utils';

// Supported formats within the editor
const FORMATS = ['bold', 'italic', 'underline', 'align', 'list'];

const RichTextEditorComponent = forwardRef<IRichTextEditorRef, IRichTextEditorProps>(
  (
    {
      value = '',
      onChange,
      height = 200,
      toolbarOptions = ['bold', 'italic', 'align'],
      isError = false,
      placeholder = 'Enter text...',
      htmlPlaceholder,
      disabled = false,
      className = '',
      dataCy,
      dataTestId,
      ...rest
    },
    ref,
  ) => {
    const quillRef = useRef<ReactQuill>(null);

    // Check if editor is empty
    const isEmpty = !value || value === '<p><br></p>' || value.trim() === '';

    // Build toolbar configuration based on toolbarOptions prop
    const toolbarConfig = useMemo(() => {
      const toolbar: (string | { [key: string]: any[] })[] = [];

      if (toolbarOptions.includes('bold')) {
        toolbar.push('bold');
      }
      if (toolbarOptions.includes('italic')) {
        toolbar.push('italic');
      }
      if (toolbarOptions.includes('underline')) {
        toolbar.push('underline');
      }
      if (toolbarOptions.includes('align')) {
        toolbar.push({ align: [] });
      }
      if (toolbarOptions.includes('list')) {
        toolbar.push({ list: [] });
      }

      return toolbar.length > 0 ? [toolbar] : false;
    }, [toolbarOptions]);

    const modules = useMemo(
      () => ({
        toolbar: toolbarConfig,
        clipboard: {
          matchVisual: false,
        },
      }),
      [toolbarConfig],
    );

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getHTMLContent: () => {
          if (!value) return '';
          // Sanitize HTML before returning
          return sanitizeHTML(value);
        },
        getPlainText: () => {
          const editor = quillRef.current?.getEditor();
          return editor?.getText() || '';
        },
        getEditor: () => {
          return quillRef.current?.getEditor();
        },
      }),
      [value],
    );

    const handleChange = useCallback(
      (content: string) => {
        if (onChange) {
          onChange(content);
        }
      },
      [onChange],
    );

    const sanitizeContent = useCallback(() => {
      if (!onChange || !value) return;

      const sanitized = sanitizeHTML(value);
      // Only update if content actually changed after sanitization
      if (sanitized !== value) {
        onChange(sanitized);
      }
    }, [onChange, value]);

    // Attach blur listeners to Quill editor instance
    useEffect(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const handleBlur = () => {
        sanitizeContent();
      };

      // Attach events to the Quill editor's root element
      const editorRoot = editor.root;
      editorRoot.addEventListener('blur', handleBlur);

      return () => {
        editorRoot.removeEventListener('blur', handleBlur);
      };
    }, [sanitizeContent]);

    // Handle HTML placeholder click to focus editor
    const handlePlaceholderClick = useCallback(() => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        editor.focus();
      }
    }, []);

    const editorHeight = typeof height === 'number' ? `${height}px` : height;

    // Show HTML placeholder when editor is empty and not focused
    const showHtmlPlaceholder = htmlPlaceholder && isEmpty && !disabled;

    return (
      <div
        className={`rich-text-editor-container ${isError ? 'is-error' : ''} ${className}`}
        style={
          { '--editor-height': editorHeight } as React.CSSProperties & { '--editor-height': string }
        }
        data-cy={dataCy}
        data-testid={dataTestId}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={FORMATS}
          placeholder={htmlPlaceholder ? '' : placeholder}
          readOnly={disabled}
          {...rest}
        />
        {showHtmlPlaceholder && (
          <div
            className="html-placeholder-overlay"
            onClick={handlePlaceholderClick}
            dangerouslySetInnerHTML={{ __html: htmlPlaceholder }}
          />
        )}
      </div>
    );
  },
);

RichTextEditorComponent.displayName = 'RichTextEditor';

export const RichTextEditor = RichTextEditorComponent;
